import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getDashboardConfig } from '../config/env';

import { useErrorHandler } from './useErrorHandler';
import { useRealTimeUpdates } from './useRealTimeUpdates';

interface DataSyncOptions {
  enableRealTime?: boolean;
  enablePeriodicSync?: boolean;
  syncInterval?: number;
  enableConflictResolution?: boolean;
  onSyncComplete?: (syncResult: SyncResult) => void;
  onSyncError?: (error: Error) => void;
}

interface SyncResult {
  timestamp: Date;
  updatedQueries: string[];
  conflicts: ConflictInfo[];
  success: boolean;
  duration: number;
}

interface ConflictInfo {
  queryKey: string[];
  localVersion: any;
  remoteVersion: any;
  resolution: 'local' | 'remote' | 'merge';
}

interface DataVersion {
  queryKey: string[];
  version: string;
  timestamp: Date;
  checksum: string;
}

/**
 * Comprehensive data synchronization hook
 * Handles real-time updates, periodic sync, and conflict resolution
 */
export function useDataSync(options: DataSyncOptions = {}) {
  const {
    enableRealTime = true,
    enablePeriodicSync = true,
    syncInterval = getDashboardConfig().refreshInterval,
    enableConflictResolution = true,
    onSyncComplete,
    onSyncError,
  } = options;

  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [syncHistory, setSyncHistory] = useState<SyncResult[]>([]);
  const [dataVersions, setDataVersions] = useState<Map<string, DataVersion>>(
    new Map()
  );

  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();
  const syncIntervalRef = useRef<NodeJS.Timeout>();
  const conflictResolutionRef = useRef<
    Map<string, 'local' | 'remote' | 'merge'>
  >(new Map());

  // Record sync result in history
  const recordSyncResult = useCallback((result: SyncResult) => {
    setSyncHistory(prev => [result, ...prev.slice(0, 9)]); // Keep last 10 results
  }, []);

  // Conflict resolution strategy
  const resolveConflict = useCallback(
    async (
      queryKey: string[],
      localData: any,
      remoteData: any
    ): Promise<'local' | 'remote' | 'merge'> => {
      const keyString = queryKey.join('_');

      // Check if user has set a preference for this query
      const userPreference = conflictResolutionRef.current.get(keyString);
      if (userPreference) {
        return userPreference;
      }

      // Default resolution strategy based on data type
      if (localData?.timestamp && remoteData?.timestamp) {
        // Use timestamp-based resolution
        return new Date(localData.timestamp) > new Date(remoteData.timestamp)
          ? 'local'
          : 'remote';
      }

      // Default to remote data for most cases
      return 'remote';
    },
    []
  );

  // Merge data strategy
  const mergeData = useCallback((localData: any, remoteData: any) => {
    // Simple merge strategy - can be enhanced based on data structure
    if (typeof localData === 'object' && typeof remoteData === 'object') {
      return {
        ...localData,
        ...remoteData,
        _merged: true,
        _mergedAt: new Date().toISOString(),
      };
    }
    return remoteData;
  }, []);

  // Handle metrics update event
  const handleMetricsUpdate = useCallback(
    async (payload: any) => {
      const { queryKeys, version, checksum } = payload;
      const conflicts: ConflictInfo[] = [];

      for (const queryKey of queryKeys) {
        const keyString = Array.isArray(queryKey)
          ? queryKey.join('_')
          : queryKey;
        const currentVersion = dataVersions.get(keyString);

        if (currentVersion && enableConflictResolution) {
          // Check for conflicts
          const localData = queryClient.getQueryData(queryKey);
          if (localData && currentVersion.checksum !== checksum) {
            const conflict: ConflictInfo = {
              queryKey,
              localVersion: localData,
              remoteVersion: payload.data,
              resolution: await resolveConflict(
                queryKey,
                localData,
                payload.data
              ),
            };
            conflicts.push(conflict);
          }
        }

        // Update version tracking
        setDataVersions(
          prev =>
            new Map(
              prev.set(keyString, {
                queryKey,
                version,
                timestamp: new Date(),
                checksum,
              })
            )
        );

        // Invalidate query to trigger refetch
        await queryClient.invalidateQueries({ queryKey });
      }

      // Record sync result
      recordSyncResult({
        timestamp: new Date(),
        updatedQueries: queryKeys.map((key: any) =>
          Array.isArray(key) ? key.join('_') : key
        ),
        conflicts,
        success: true,
        duration: 0,
      });
    },
    [
      queryClient,
      dataVersions,
      enableConflictResolution,
      resolveConflict,
      recordSyncResult,
    ]
  );

  // Handle data sync completed event
  const handleDataSyncCompleted = useCallback(
    async (payload: any) => {
      const { source, syncType, affectedQueries } = payload;

      // Invalidate affected queries
      for (const queryKey of affectedQueries) {
        await queryClient.invalidateQueries({ queryKey });
      }

      setLastSyncTime(new Date());
    },
    [queryClient]
  );

  // Handle conflict detection
  const handleConflictDetected = useCallback(
    async (payload: any) => {
      const { queryKey, localData, remoteData } = payload;

      if (enableConflictResolution) {
        const resolution = await resolveConflict(
          queryKey,
          localData,
          remoteData
        );

        switch (resolution) {
          case 'local':
            // Keep local data, ignore remote
            break;
          case 'remote':
            // Accept remote data
            queryClient.setQueryData(queryKey, remoteData);
            break;
          case 'merge': {
            // Merge data (implementation depends on data structure)
            const mergedData = mergeData(localData, remoteData);
            queryClient.setQueryData(queryKey, mergedData);
            break;
          }
        }
      }
    },
    [queryClient, enableConflictResolution, resolveConflict, mergeData]
  );

  // Handle real-time events
  const handleRealTimeEvent = useCallback(
    async (event: any) => {
      const { type, payload } = event;

      switch (type) {
        case 'metrics_updated':
          await handleMetricsUpdate(payload);
          break;
        case 'data_sync_completed':
          await handleDataSyncCompleted(payload);
          break;
        case 'conflict_detected':
          await handleConflictDetected(payload);
          break;
        default:
          console.log('Unknown real-time event:', type);
      }
    },
    [handleMetricsUpdate, handleDataSyncCompleted, handleConflictDetected]
  );

  // Real-time updates (disabled by default to prevent connection errors)
  const { isConnected: isRealTimeConnected } = useRealTimeUpdates({
    enabled: enableRealTime,
    onEvent: handleRealTimeEvent,
    onError: () => {
      // Silently handle real-time errors when backend is not available
    },
  });

  // Manual sync trigger
  const triggerSync = useCallback(async () => {
    if (isSyncing) {
      return;
    }

    setIsSyncing(true);
    const startTime = Date.now();
    const updatedQueries: string[] = [];
    const conflicts: ConflictInfo[] = [];

    try {
      // Get all active queries
      const queryCache = queryClient.getQueryCache();
      const queries = queryCache.getAll();

      // Refetch all stale queries
      for (const query of queries) {
        if (query.isStale()) {
          try {
            await query.fetch();
            updatedQueries.push(query.queryKey.join('_'));
          } catch (error) {
            console.error('Failed to sync query:', query.queryKey, error);
          }
        }
      }

      const syncResult: SyncResult = {
        timestamp: new Date(),
        updatedQueries,
        conflicts,
        success: true,
        duration: Date.now() - startTime,
      };

      recordSyncResult(syncResult);
      setLastSyncTime(new Date());
      onSyncComplete?.(syncResult);
    } catch (error) {
      const syncResult: SyncResult = {
        timestamp: new Date(),
        updatedQueries,
        conflicts,
        success: false,
        duration: Date.now() - startTime,
      };

      recordSyncResult(syncResult);
      handleError(error, { context: 'manual_sync' });
      onSyncError?.(error as Error);
    } finally {
      setIsSyncing(false);
    }
  }, [
    isSyncing,
    queryClient,
    handleError,
    onSyncComplete,
    onSyncError,
    recordSyncResult,
  ]);

  // Set conflict resolution preference
  const setConflictResolution = useCallback(
    (queryKey: string[], resolution: 'local' | 'remote' | 'merge') => {
      const keyString = queryKey.join('_');
      conflictResolutionRef.current.set(keyString, resolution);
    },
    []
  );

  // Periodic sync setup
  useEffect(() => {
    if (enablePeriodicSync && syncInterval > 0) {
      syncIntervalRef.current = setInterval(() => {
        if (!isSyncing) {
          triggerSync();
        }
      }, syncInterval);

      return () => {
        if (syncIntervalRef.current) {
          clearInterval(syncIntervalRef.current);
        }
      };
    }
  }, [enablePeriodicSync, syncInterval, isSyncing, triggerSync]);

  // Get sync statistics
  const getSyncStats = useCallback(() => {
    const successfulSyncs = syncHistory.filter(sync => sync.success).length;
    const failedSyncs = syncHistory.filter(sync => !sync.success).length;
    const averageDuration =
      syncHistory.length > 0
        ? syncHistory.reduce((sum, sync) => sum + sync.duration, 0) /
          syncHistory.length
        : 0;

    return {
      totalSyncs: syncHistory.length,
      successfulSyncs,
      failedSyncs,
      successRate:
        syncHistory.length > 0
          ? (successfulSyncs / syncHistory.length) * 100
          : 0,
      averageDuration,
      lastSyncTime,
    };
  }, [syncHistory, lastSyncTime]);

  return {
    // Status
    isSyncing,
    isRealTimeConnected,
    lastSyncTime,

    // Actions
    triggerSync,
    setConflictResolution,

    // Data
    syncHistory,
    getSyncStats,

    // Configuration
    enableRealTime,
    enablePeriodicSync,
    syncInterval,
  };
}

/**
 * Hook for monitoring data freshness and staleness
 */
export function useDataFreshness() {
  const queryClient = useQueryClient();
  const [staleQueries, setStaleQueries] = useState<string[]>([]);

  // Check for stale queries
  const checkStaleness = useCallback(() => {
    const queryCache = queryClient.getQueryCache();
    const queries = queryCache.getAll();

    const stale = queries
      .filter(query => query.isStale())
      .map(query => query.queryKey.join('_'));

    setStaleQueries(stale);
  }, [queryClient]);

  // Periodic staleness check
  useEffect(() => {
    const interval = setInterval(checkStaleness, 30000); // Check every 30 seconds
    checkStaleness(); // Initial check

    return () => clearInterval(interval);
  }, [checkStaleness]);

  // Get data age for a specific query
  const getDataAge = useCallback(
    (queryKey: string[]) => {
      const query = queryClient.getQueryCache().find({ queryKey });
      if (query?.state.dataUpdatedAt) {
        return Date.now() - query.state.dataUpdatedAt;
      }
      return null;
    },
    [queryClient]
  );

  // Check if data is fresh
  const isDataFresh = useCallback(
    (queryKey: string[], maxAge: number = 5 * 60 * 1000) => {
      const age = getDataAge(queryKey);
      return age !== null && age < maxAge;
    },
    [getDataAge]
  );

  return {
    staleQueries,
    getDataAge,
    isDataFresh,
    checkStaleness,
    totalStaleQueries: staleQueries.length,
  };
}
